import { Box, Dropdown, List, sprinkles, Text, Tooltip } from "@saleor/macaw-ui-next";
import { type ReactElement, type ReactNode, useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

import { useMenuStructure } from "./hooks/useMenuStructure";
import { type SidebarMenuItem } from "./types";
import { isMenuActive } from "./utils";

const getAccessibleLabel = (menuItem: SidebarMenuItem): string =>
  typeof menuItem.label === "string" ? menuItem.label : menuItem.id;

const MenuItemText = ({
  children,
  fontWeight,
}: {
  children: ReactNode;
  fontWeight?: "bold";
}): ReactElement =>
  typeof children === "string" ? <Text fontWeight={fontWeight}>{children}</Text> : <>{children}</>;

const CollapsedSingleItem = ({ menuItem }: { menuItem: SidebarMenuItem }): ReactElement => {
  const location = useLocation();
  const active = isMenuActive(location.pathname, menuItem);
  const label = getAccessibleLabel(menuItem);

  return (
    <List.Item
      active={active}
      borderRadius={3}
      padding={2}
      onClick={menuItem.onClick}
      data-test-id={`collapsed-menu-item-${menuItem.id}`}
      position="relative"
    >
      <Tooltip>
        <Tooltip.Trigger>
          <Link
            to={menuItem.url || ""}
            replace={active}
            aria-label={label}
            className={sprinkles({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            })}
          >
            {menuItem.icon}
          </Link>
        </Tooltip.Trigger>
        <Tooltip.Content side="right">
          <Tooltip.Arrow />
          <MenuItemText>{menuItem.label}</MenuItemText>
        </Tooltip.Content>
      </Tooltip>
      {menuItem.endAdornment && (
        <Box position="absolute" right={0} top={0}>
          {menuItem.endAdornment}
        </Box>
      )}
    </List.Item>
  );
};

const CollapsedGroupItem = ({ menuItem }: { menuItem: SidebarMenuItem }): ReactElement => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const active =
    isMenuActive(location.pathname, menuItem) ||
    Boolean(menuItem.children?.some(child => isMenuActive(location.pathname, child)));
  const label = getAccessibleLabel(menuItem);

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <Dropdown.Trigger>
        <Box
          as="button"
          type="button"
          borderWidth={0}
          borderRadius={3}
          padding={2}
          cursor="pointer"
          backgroundColor={active ? "default1Pressed" : "transparent"}
          position="relative"
          __width="100%"
          aria-label={label}
          data-test-id={`collapsed-menu-group-${menuItem.id}`}
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            {menuItem.icon}
          </Box>
          {menuItem.endAdornment && (
            <Box position="absolute" right={0} top={0}>
              {menuItem.endAdornment}
            </Box>
          )}
        </Box>
      </Dropdown.Trigger>
      <Dropdown.Content side="right" align="start">
        <Box
          __minWidth={220}
          backgroundColor="default1"
          borderRadius={4}
          boxShadow="defaultOverlay"
          padding={2}
        >
          <List>
            {menuItem.url && (
              <Dropdown.Item>
                <List.Item borderRadius={3}>
                  <Link
                    to={menuItem.url}
                    onClick={() => {
                      menuItem.onClick?.();
                      setOpen(false);
                    }}
                    className={sprinkles({
                      display: "block",
                      width: "100%",
                      paddingX: 2,
                      paddingY: 2,
                    })}
                  >
                    <MenuItemText fontWeight="bold">{menuItem.label}</MenuItemText>
                  </Link>
                </List.Item>
              </Dropdown.Item>
            )}
            {menuItem.children?.map(child =>
              child.type === "divider" ? (
                <List.Divider key={child.id} paddingX={2} paddingY={1.5}>
                  <Text size={1} color="default2">
                    {child.label}
                  </Text>
                </List.Divider>
              ) : (
                <Dropdown.Item key={child.id}>
                  <List.Item active={isMenuActive(location.pathname, child)} borderRadius={3}>
                    <Link
                      to={child.url || ""}
                      onClick={() => {
                        child.onClick?.();
                        setOpen(false);
                      }}
                      className={sprinkles({
                        display: "block",
                        width: "100%",
                        paddingX: 2,
                        paddingY: 2,
                      })}
                    >
                      <MenuItemText>{child.label}</MenuItemText>
                    </Link>
                  </List.Item>
                </Dropdown.Item>
              ),
            )}
          </List>
        </Box>
      </Dropdown.Content>
    </Dropdown>
  );
};

export const CollapsedMenu = (): ReactElement => {
  const menuStructure = useMenuStructure();

  return (
    <List
      as="ol"
      display="grid"
      gap={1}
      paddingX={2}
      overflowY="auto"
      style={{ alignContent: "start", gridAutoRows: "min-content" }}
      data-test-id="collapsed-menu-list"
    >
      {menuStructure.map(menuItem => {
        if (menuItem.type === "divider") {
          return <List.Divider key={menuItem.id} paddingY={1} />;
        }

        return menuItem.type === "itemGroup" ? (
          <CollapsedGroupItem key={menuItem.id} menuItem={menuItem} />
        ) : (
          <CollapsedSingleItem key={menuItem.id} menuItem={menuItem} />
        );
      })}
    </List>
  );
};
